import { ConsoleID, getConsoleState } from 'app/config/Logger';
import { Cls, Emojis } from 'app/constants';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { getIsIndexDBInitialized } from 'app/slices/AppUiSlice';
import { getDBsInitState, initializeSyncManager, setSyncManagerWithIDBData } from 'app/slices/SyncManagerSlice';
import { loadSyncStatusFromDB } from 'lib/idb/IndexDbInterface';
import { useCallback, useEffect } from 'react';
import { Stores, SyncIDBPayload } from 'types';

const CONSOLE_ON = getConsoleState(ConsoleID.UseSyncManager);

const useSyncManager = () => {
  const dispatch = useAppDispatch();
  const isDBInitialized = useAppSelector(getIsIndexDBInitialized);

  const dbsInitState = useAppSelector(getDBsInitState);

  useEffect(() => {
    if (isDBInitialized) {
      dispatch(initializeSyncManager());
    }
  }, [dispatch, isDBInitialized]); // ✅ Redux thunk handles deduplication

  const refreshSyncStatus = useCallback(
    async (year: string = '2024') => {
      try {
        const msg = `${Emojis.eyes} %c REFRESHING %c Sync status from %c IndexedDB %c`;
        if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '');

        await loadSyncStatusFromDB({
          storeName: Stores.SyncStatus,
          callbackFn: (data: SyncIDBPayload) => {
            dispatch(setSyncManagerWithIDBData(data));
            const msg = `${Emojis.checkBox} %c REFRESHED %c Sync status from %c IndexedDB %c`;
            if (CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '');
          },
          shouldFetch: false,
          year,
        });
      } catch (error) {
        if (CONSOLE_ON) console.error(`${Emojis.ex} %c FAILED %c to refresh Sync status: ${error}`, Cls.Cri, '');
      }
    },
    [dispatch]
  );

  const initComplete = dbsInitState.indexDBInit && dbsInitState.firebaseDBInit;

  useEffect(() => {
    const msg = `${Emojis.checkBox} %c INITIALIZED %c %c SyncManager %c`;
    if (initComplete && CONSOLE_ON) console.log(msg, Cls.Act, '', Cls.Loc, '');
  }, [initComplete]);

  return {
    initComplete,
    refreshSyncStatus,
  };
};

export default useSyncManager;
